import { FormEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'

import { suppliersManagementActions } from '../SuppliersManagementSagas'
import { selectSuppliersManagementCountrySites } from '../SuppliersManagementSelectors'
import { CountrySite, SupplierFormErrors } from '../types'

interface UseAddModalParams {
  isOpen: boolean
  close: () => void
}

interface UseAddModalReturn {
  t: TFunction
  name: string
  siteId: string
  types: string[]
  availableTypes: string[]
  countrySites: CountrySite[]
  formErrors: SupplierFormErrors
  setName: (name: string) => void
  selectSite: (siteId: string) => void
  toggleType: (type: string) => void
  submitHandler: (event: FormEvent) => void
}

const useAddModal = ({ isOpen, close }: UseAddModalParams): UseAddModalReturn => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const countrySites = useSelector(selectSuppliersManagementCountrySites)

  const [name, setName] = useState('')
  const [siteId, setSiteId] = useState('')
  const [types, setTypes] = useState<string[]>([])
  const [formErrors, setFormErrors] = useState<SupplierFormErrors>({})

  const availableTypes = countrySites.find(site => String(site.id) === siteId)?.types ?? []

  const selectSite = (selectedSiteId: string) => {
    setSiteId(selectedSiteId)
    setTypes([])
  }

  const toggleType = (type: string) => {
    setTypes(currentTypes =>
      currentTypes.includes(type) ? currentTypes.filter(current => current !== type) : [...currentTypes, type],
    )
  }

  const submitHandler = (event: FormEvent) => {
    event.preventDefault()
    dispatch({
      type: suppliersManagementActions.CREATE_SUPPLIER,
      supplierData: { name, site_id: Number(siteId), types },
      setFormErrors,
      onSuccess: close,
    })
  }

  useEffect(() => {
    if (isOpen) return
    setName('')
    setSiteId('')
    setTypes([])
    setFormErrors({})
  }, [isOpen])

  useEffect(() => {
    if (siteId || countrySites.length !== 1) return
    setSiteId(String(countrySites[0].id))
  }, [countrySites])

  return {
    t,
    name,
    siteId,
    types,
    availableTypes,
    countrySites,
    formErrors,
    setName,
    selectSite,
    toggleType,
    submitHandler,
  }
}

export default useAddModal
